import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, CircleCheckBig, Construction, Search, TriangleAlert, Upload } from 'lucide-react';
import * as hr from '../../../api';
import Modal from '../../../components/Modal';
import { Select } from '../../../components/Controls';
import { AChip, NOT_YET, ngn, num, rid } from './shared';
import Loader from '../../../components/Loader';

// Section C pickers (Stephanet's hrrc/adv/AdvPickers.jsx): import lines from Excel / CSV, the material master, the vendor master, a
// previous request, line details. Inventory stock, the asset register and the fleet register are not built yet.

const TEMPLATE = ['Category', 'Item Name / Description', 'Specification', 'Brand / Model', 'Unit', 'Quantity', 'Unit Cost', 'Currency', 'Vendor', 'Required Date',
  'Cost Code', 'Last Purchased Date', 'Last Quantity', 'Last Rate', 'Last Vendor', 'Remarks'];

export const emptyLine = () => ({
  _k: rid(), line_category: 'Material', material_id: '', item_code: '', item_name: '', description: '', specification: '', brand_model: '', uom_id: '',
  quantity: 1, unit_cost: 0, currency: 'NGN', supplier_id: '', vendor_name: '', vendor: '', last_purchased_date: '', last_purchased_quantity: '',
  last_purchased_unit_cost: '', last_purchased_vendor: '', site_id: '', cost_code: '', ledger_id: '', budget_available: '', required_date: '', remarks: '',
  status: 'Pending', source: 'Manual', source_request_id: '',
});

export function downloadLineTemplate(categories) {
  const rows = [TEMPLATE,
    ['Tool', 'Cordless Drilling Machine', '20V Brushless', 'Bosch GSB 18V', 'Each', '2', '185000', 'NGN', 'ABC Tools Ltd', '2026-06-30', '', '2026-03-12', '1', '178000', 'ABC Tools Ltd', 'For site drilling work'],
    ['Material', '2.5mm Cable', 'Pure copper', 'Coleman', 'Roll', '10', '48000', 'NGN', 'Electrical Hub Ltd', '', '', '2026-05-10', '8', '45000', '', 'Wiring installation'],
    [`NOTE: Category options: ${categories.join(' / ')}. Unit is a unit of measure's symbol or name.`]];
  const esc = v => `"${String(v).replace(/"/g, '""')}"`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([rows.map(r => r.map(esc).join(',')).join('\n')], { type: 'text/csv' }));
  a.download = 'request_lines_template.csv';
  a.click();
}

export function parseCsv(text) {
  const out = [];
  let row = []; let cur = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; } else if (c === '"') q = false; else cur += c; }
    else if (c === '"') q = true;
    else if (c === ',') { row.push(cur); cur = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(cur); out.push(row); row = []; cur = ''; }
    else cur += c;
  }
  if (cur || row.length) { row.push(cur); out.push(row); }
  return out.filter(r => r.some(x => x.trim()));
}

export function ImportLinesModal({ meta, onClose, onImport }) {
  const [rows, setRows] = useState(null);
  const [errs, setErrs] = useState([]);
  const uomOf = u => meta.uoms.find(x => [x.symbol, x.name].some(n => (n || '').toLowerCase() === (u || '').trim().toLowerCase()));
  const read = file => {
    const fr = new FileReader();
    fr.onload = () => {
      const all = parseCsv(String(fr.result));
      if (!all.length) { setRows([]); return; }
      const head = all[0].map(x => x.trim().toLowerCase());
      const get = (r, name) => { const i = head.indexOf(name.toLowerCase()); return i >= 0 ? (r[i] || '').trim() : ''; };
      const lines = []; const errors = [];
      all.slice(1).filter(r => !String(r[0] || '').startsWith('NOTE')).forEach((r, i) => {
        const unit = get(r, 'Unit');
        const l = {
          ...emptyLine(), line_category: meta.line_categories.includes(get(r, 'Category')) ? get(r, 'Category') : 'Material', item_name: get(r, 'Item Name / Description'),
          specification: get(r, 'Specification'), brand_model: get(r, 'Brand / Model'), uom_id: uomOf(unit)?.id || '', quantity: get(r, 'Quantity'), unit_cost: get(r, 'Unit Cost'),
          currency: meta.currencies.includes(get(r, 'Currency')) ? get(r, 'Currency') : 'NGN', vendor_name: get(r, 'Vendor'), required_date: get(r, 'Required Date'),
          cost_code: get(r, 'Cost Code'), last_purchased_date: get(r, 'Last Purchased Date'), last_purchased_quantity: get(r, 'Last Quantity'),
          last_purchased_unit_cost: get(r, 'Last Rate'), last_purchased_vendor: get(r, 'Last Vendor'), remarks: get(r, 'Remarks'), source: 'Excel Import',
        };
        const rowNo = i + 2;
        if (!l.item_name) errors.push({ row: rowNo, field: 'Item Name', msg: 'Required field is empty' });
        if (!unit) errors.push({ row: rowNo, field: 'Unit', msg: 'Unit is required' });
        else if (!l.uom_id) errors.push({ row: rowNo, field: 'Unit', msg: `"${unit}" is not in the unit-of-measure list` });
        if (!(num(l.quantity) > 0)) errors.push({ row: rowNo, field: 'Quantity', msg: 'Quantity must be > 0' });
        if (l.unit_cost !== '' && Number.isNaN(Number(l.unit_cost))) errors.push({ row: rowNo, field: 'Unit Cost', msg: 'Invalid unit cost' });
        if (l.required_date && !/^\d{4}-\d{2}-\d{2}$/.test(l.required_date)) errors.push({ row: rowNo, field: 'Required Date', msg: 'Use YYYY-MM-DD' });
        if (l.last_purchased_date && !/^\d{4}-\d{2}-\d{2}$/.test(l.last_purchased_date)) errors.push({ row: rowNo, field: 'Last Purchased Date', msg: 'Use YYYY-MM-DD' });
        l._row = rowNo;
        l.quantity = num(l.quantity); l.unit_cost = num(l.unit_cost);
        lines.push(l);
      });
      setRows(lines); setErrs(errors);
    };
    fr.readAsText(file);
  };
  const bad = new Set(errs.map(e => e.row));
  const valid = (rows || []).filter(r => !bad.has(r._row));
  return (
    <Modal wide title="Import Lines from Excel / CSV" onClose={onClose}
           footer={<><button className="mdr-btn" onClick={onClose}>Cancel</button>
             <button className="mds-run" disabled={!valid.length} onClick={() => onImport(valid.map(({ _row, ...l }) => l))}><CircleCheckBig size={13} /> Import {valid.length} valid line(s)</button></>}>
      <label className="dropzone"><Upload size={20} /><span><b>Choose the filled template (.csv)</b><small>Save the Excel template as CSV, then pick it here</small></span>
        <input type="file" accept=".csv,text/csv" hidden onChange={e => { if (e.target.files[0]) read(e.target.files[0]); e.target.value = ''; }} /></label>
      {rows && (<>
        {errs.length > 0 && <div className="adv-errbox"><p><TriangleAlert size={13} /> {errs.length} error(s)</p>{errs.slice(0, 12).map((e, i) => <div key={i}>Row {e.row} · {e.field}: {e.msg}</div>)}</div>}
        <p className="hrc-sub">{rows.length} row(s) read · <b>{valid.length} valid</b> | <span className="rd">{bad.size} with errors</span></p>
        <div className="mdr-scroll adv-pick-scroll"><table className="mdr-table mds-table"><thead><tr><th>Row</th><th>Item Name</th><th>Unit</th><th>Qty</th><th>Unit Cost</th><th>Status</th></tr></thead>
          <tbody>{rows.map(r => <tr key={r._k} className={bad.has(r._row) ? 'adv-badrow' : ''}><td>{r._row}</td><td>{r.item_name || <i>MISSING</i>}</td>
            <td>{meta.uoms.find(u => u.id === r.uom_id)?.symbol || '—'}</td><td>{r.quantity}</td><td>{ngn(r.unit_cost)}</td>
            <td>{bad.has(r._row) ? <span className="mdr-chip t-red">Error</span> : <span className="mdr-chip t-green">Valid</span>}</td></tr>)}</tbody></table></div>
      </>)}
    </Modal>
  );
}

function useLookup(kind, q) {
  const [rows, setRows] = useState(null);
  useEffect(() => { const t = setTimeout(() => hr.advLookup(kind, q).then(setRows).catch(() => setRows([])), 250); return () => clearTimeout(t); }, [kind, q]);
  return rows;
}

function PickModal({ title, q, setQ, placeholder, children, extra, onClose }) {
  return (
    <Modal wide className="adv-pick" title={title} onClose={onClose} footer={<button className="mdr-btn" onClick={onClose}>Close</button>}>
      <div className="ts-filters"><div className="mdr-search"><Search size={14} /><input autoFocus placeholder={placeholder} value={q} onChange={e => setQ(e.target.value)} /></div>{extra}</div>
      <div className="mdr-scroll adv-pick-scroll">{children}</div>
    </Modal>
  );
}

const NOT_YET_TITLE = { inventory: 'Inventory Register', assets: 'Asset Register', fleet: 'Fleet Register' };
export function NotYetModal({ kind, onClose }) {
  return (
    <Modal title={`Select from ${NOT_YET_TITLE[kind]}`} onClose={onClose} footer={<button className="mds-run" onClick={onClose}>Close</button>}>
      <div className="sd-empty"><Construction size={22} /><b>{NOT_YET_TITLE[kind]}: not available yet</b><p>{NOT_YET} Until then, add the line by hand, from the material master or from a previous request.</p></div>
    </Modal>
  );
}

export function MaterialModal({ onClose, onSelect }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [qty, setQty] = useState({});
  const rows = useLookup('materials', q);
  const cats = useMemo(() => [...new Set((rows || []).map(r => r.category).filter(Boolean))], [rows]);
  const list = (rows || []).filter(r => !cat || r.category === cat);
  const pick = r => onSelect({
    line_category: 'Material', material_id: r.material_id, item_code: r.item_code, item_name: r.item_name, uom_id: r.uom_id || '', quantity: num(qty[r.material_id]) || 1,
    unit_cost: r.last_rate || r.standard_rate || 0, last_purchased_date: r.last_date || '', last_purchased_quantity: r.last_qty ?? '', last_purchased_unit_cost: r.last_rate ?? '',
    last_purchased_vendor: r.last_vendor || '', source: 'Material Master',
  });
  return (
    <PickModal title="Select from Material Master" q={q} setQ={setQ} placeholder="Search material name or code..." onClose={onClose}
               extra={<Select value={cat} onChange={e => setCat(e.target.value)}><option value="">All categories</option>{cats.map(c => <option key={c} value={c}>{c}</option>)}</Select>}>
      <table className="mdr-table mds-table act-first"><thead><tr>{['', 'Code', 'Material', 'Category', 'Unit', 'Standard Rate', 'Last Date', 'Last Qty', 'Last Rate', 'Last Vendor', 'Qty'].map(x => <th key={x}>{x}</th>)}</tr></thead>
        <tbody>{list.map(r => (
          <tr key={r.material_id}>
            <td><button className="mds-view" onClick={() => pick(r)}>Select</button></td>
            <td className="mono">{r.item_code}</td><td><b>{r.item_name}</b></td><td>{r.category || '—'}</td><td>{r.unit}</td><td>{ngn(r.standard_rate)}</td>
            <td>{r.last_date || '—'}</td><td>{r.last_qty ?? '—'}</td><td>{r.last_rate ? ngn(r.last_rate) : '—'}</td><td>{r.last_vendor || '—'}</td>
            <td><input type="number" min="0.1" step="0.1" className="adv-qty" value={qty[r.material_id] ?? 1} onChange={e => setQty(s => ({ ...s, [r.material_id]: e.target.value }))} /></td>
          </tr>))}
          {rows && !list.length && <tr><td colSpan={11} className="mdr-empty">No materials found.</td></tr>}
          {!rows && <tr><td colSpan={11}><Loader title="Loading…" /></td></tr>}</tbody></table>
    </PickModal>
  );
}

export function VendorModal({ onClose, onSelect, all, setAll }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const rows = useLookup('vendors', q);
  const cats = [...new Set((rows || []).map(r => r.category).filter(Boolean))];
  return (
    <PickModal title="Select from Vendor Master" q={q} setQ={setQ} placeholder="Search vendor name or code..." onClose={onClose}
               extra={<><Select value={cat} onChange={e => setCat(e.target.value)}><option value="">All categories</option>{cats.map(c => <option key={c} value={c}>{c}</option>)}</Select>
                 {setAll && <label className="hrc-inline"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> Apply to all lines</label>}</>}>
      <table className="mdr-table mds-table act-first"><thead><tr>{['', 'Code', 'Vendor', 'Category', 'Phone', 'Email', 'Bank', 'Rating', 'Last Item', 'Last Rate', 'Last Date', 'Status'].map(x => <th key={x}>{x}</th>)}</tr></thead>
        <tbody>{(rows || []).filter(r => !cat || r.category === cat).map(r => (
          <tr key={r.supplier_id}><td><button className="mds-view" onClick={() => onSelect(r)}>Select</button></td><td className="mono">{r.vendor_code}</td><td className="b">{r.vendor_name}</td>
            <td>{r.category || '—'}</td><td>{r.phone || '—'}</td><td>{r.email || '—'}</td><td>{r.bank_name || '—'}</td><td>{r.rating ? '★'.repeat(Math.round(r.rating)) : '—'}</td>
            <td>{r.last_supplied_item || '—'}</td><td>{r.last_rate ? ngn(r.last_rate) : '—'}</td><td>{r.last_transaction_date || '—'}</td><td><AChip text={r.status} /></td></tr>))}
          {rows && !rows.length && <tr><td colSpan={12} className="mdr-empty">No vendors found.</td></tr>}
          {!rows && <tr><td colSpan={12}><Loader title="Loading…" /></td></tr>}</tbody></table>
    </PickModal>
  );
}

export function PreviousRequestModal({ onClose, onCopy }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const rows = useLookup('previous', q);
  const copy = r => onCopy(r.lines.map(({ id, line_no, total_cost, status, source, source_request_id, source_ref, ...l }) => ({
    ...l, vendor: l.vendor || '', source: 'Previous Request', source_request_id: r.id })), r.request_no);
  return (
    <PickModal title="Select from Previous Requests" q={q} setQ={setQ} placeholder="Search ref or type..." onClose={onClose}>
      {(rows || []).map(r => (
        <div key={r.id} className="adv-prev">
          <div className="adv-prev-head" onClick={() => setOpen(open === r.id ? null : r.id)}>
            <span className="mono">{r.request_no}</span><span>{r.request_date}</span><b>{r.request_type}</b><span>{r.requested_by}</span><span>{r.project_site || r.department}</span>
            <span className="b">{ngn(r.total_requested)}</span><AChip text={r.status} /><span>{r.lines.length} line(s)</span>
            <button className="mds-view" onClick={e => { e.stopPropagation(); copy(r); }}>Copy Lines</button>
            {open === r.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </div>
          {open === r.id && <table className="mdr-table mds-table"><thead><tr><th>#</th><th>Category</th><th>Item</th><th>Unit</th><th>Qty</th><th>Unit Cost</th><th>Total</th><th>Vendor</th></tr></thead>
            <tbody>{r.lines.map((l, i) => <tr key={l.id}><td>{i + 1}</td><td>{l.line_category}</td><td>{l.item_name}</td><td>{l.unit || '—'}</td><td>{l.quantity}</td><td>{ngn(l.unit_cost)}</td>
              <td>{ngn(l.total_cost)}</td><td>{l.vendor || '—'}</td></tr>)}</tbody></table>}
        </div>
      ))}
      {rows && !rows.length && <p className="mdr-empty">No previous requests with line items.</p>}
      {!rows && <Loader title="Loading…" />}
    </PickModal>
  );
}

export function ViewLineModal({ meta, line, lineNo, onClose }) {
  const name = (list, id) => (list || []).find(x => String(x.id) === String(id))?.name;
  const rows = [['Line No', lineNo], ['Category', line.line_category], ['Item Code', line.item_code], ['Item Name', line.item_name], ['Specification', line.specification],
    ['Brand / Model', line.brand_model], ['Unit', meta.uoms.find(u => String(u.id) === String(line.uom_id))?.symbol], ['Quantity', line.quantity], ['Unit Cost', ngn(line.unit_cost)],
    ['Total Cost', ngn(num(line.quantity) * num(line.unit_cost))], ['Currency', line.currency], ['Vendor', line.vendor || line.vendor_name],
    ['Project / Site', name(meta.sites, line.site_id)], ['Cost Code', line.cost_code], ['GL Code', name(meta.ledgers, line.ledger_id)],
    ['Budget Available', line.budget_available ? ngn(line.budget_available) : ''], ['Required Date', line.required_date], ['Last Purchased Date', line.last_purchased_date],
    ['Last Quantity', line.last_purchased_quantity], ['Last Rate', line.last_purchased_unit_cost ? ngn(line.last_purchased_unit_cost) : ''], ['Last Vendor', line.last_purchased_vendor],
    ['Source', line.source], ['Remarks', line.remarks], ['Status', line.status]];
  return (
    <Modal title={`Line #${lineNo} Details — ${line.item_name || ''}`} onClose={onClose} footer={<button className="mds-run" onClick={onClose}>Close</button>}>
      <table className="wh-view"><tbody>{rows.filter(([, v]) => v !== '' && v !== null && v !== undefined).map(([k, v]) => <tr key={k}><th>{k}</th><td>{String(v)}</td></tr>)}</tbody></table>
    </Modal>
  );
}
